'use client'

import { useRef } from 'react'
import Link from 'next/link'
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'

interface NavSubmenuSection {
  title: string
  items: any[]
  getItemId: (item: any) => string
  getItemName: (item: any) => string
  getItemUrl: (item: any) => string
  limit?: number
}

interface NavSubmenuProps {
  triggerLabel: string
  // When set, the trigger is an anchor: hover still opens the submenu, click navigates. Omit it on touch surfaces, where a tap must expand rather than leave.
  triggerHref?: string
  sections: NavSubmenuSection[]
  width?: string
  viewAllUrl?: string
  viewAllText?: string
  triggerClassName?: string
}

const NavSubmenu = ({
  triggerLabel,
  triggerHref,
  sections,
  viewAllUrl,
  viewAllText = 'View All →',
  triggerClassName = 'text-label-md-medium leading-6 text-gray-700 !bg-transparent hover:!text-primary-600 transition-colors !rounded-none !border-b-0 nav-tab-indicator data-[state=open]:!text-primary-600',
}: NavSubmenuProps) => {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <NavigationMenuItem>
      {triggerHref ? (
        <NavigationMenuTrigger asChild className={triggerClassName}>
          <Link href={triggerHref}>{triggerLabel}</Link>
        </NavigationMenuTrigger>
      ) : (
        <NavigationMenuTrigger className={triggerClassName}>
          {triggerLabel}
        </NavigationMenuTrigger>
      )}
      {/* forceMount puts the links in the SSR HTML for crawlers; radix would otherwise only mount this on open. data-[state=closed]:hidden is required - the content is absolutely positioned and would otherwise render visible. */}
      <NavigationMenuContent
        ref={contentRef}
        forceMount
        className="data-[state=closed]:hidden"
        onPointerDownOutside={(event) => {
          // Every submenu stays mounted, so a press inside the open one counts as "outside" its closed siblings; their dismiss would hide the link before mouseup and the click would never reach it.
          if (contentRef.current?.dataset.state === 'closed') {
            event.preventDefault()
          }
        }}
      >
        <div className="py-5 px-7 whitespace-nowrap">
          <div className="flex gap-10">
            {sections.map((section, sectionIndex) => (
              <div key={section.title}>
                <h4 className="text-[13px] font-bold text-primary-600 mb-4 tracking-wider uppercase">
                  {section.title}
                </h4>
                <div className="flex flex-col">
                  {section.items?.slice(0, section.limit || 8)?.map((item) => (
                    <NavigationMenuLink asChild key={section.getItemId(item)}>
                      <Link
                        href={section.getItemUrl(item)}
                        className="block py-[6px] text-[13px] text-gray-800 hover:text-gray-950 hover:[text-shadow:0.3px_0_0_currentColor] transition-colors"
                      >
                        {section.getItemName(item)}
                      </Link>
                    </NavigationMenuLink>
                  ))}
                  {(!section.items || section.items.length === 0) && (
                    <p className="text-[13px] text-gray-400">Loading...</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {viewAllUrl && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <NavigationMenuLink asChild>
                <Link
                  href={viewAllUrl}
                  className="text-[13px] text-primary-600 font-semibold hover:text-primary-700 transition-colors"
                >
                  {viewAllText}
                </Link>
              </NavigationMenuLink>
            </div>
          )}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  )
}

export default NavSubmenu
